import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const escape = (value) => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const rate = count => count >= 3 ? 15 : count === 2 ? 10 : 5;
const MAX_ITEMS = 20;
const COLLECTION_PAGE_SIZE = 12;
const MIN_CATEGORY_CHOICES = 6;
const INITIAL_CATEGORY_PAGES = 3;
const root = () => window.Shopify?.routes?.root || '/';
const sizeKey = value => String(value || '').replace(/\s/g, '').toUpperCase();
const imageUrl = (product, variant) => variant.featured_image?.src || product.featured_image || product.images?.[0] || '';
const sizeIndex = product => product.options.findIndex(option => /size/i.test(option.name || option));
const variantSize = (product, variant) => variant?.options?.[sizeIndex(product)] || '';
const selectedCategory = (product, variant) => {
  const option = String(variant?.option1 || '').toLowerCase();
  const text = /^(shirt|pants|shorts|hoodie|sweatshirt|jacket|coat|t-?shirt|tee)/i.test(option) ? option : product.title.toLowerCase();
  if (/set|suit|2-piece|two-piece/.test(text)) return 'set';
  if (/pants|trousers|jeans|sweatpants/.test(text)) return 'pants';
  if (/shorts/.test(text)) return 'shorts';
  if (/hoodie|sweatshirt|jacket|coat/.test(text)) return 'hoodie';
  if (/t-shirt|tee/.test(text)) return 'tee';
  return 'shirt';
};

class BtkMixMatch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.product = JSON.parse(this.querySelector('[data-btk-product]').textContent);
    this.t = JSON.parse(this.querySelector('[data-btk-translations]').textContent);
    this.baseId = Number(this.dataset.variantId);
    this.count = 1;
    this.extras = [];
    this.catalog = null;
    this.collectionCache = new Map();
    this.activeCategory = 'recommended';
    this.categoryPage = 0;
    this.pickerGeneration = 0;
    this.abort = new AbortController();
    this.section = this.closest('.shopify-section');
    this.details = this.closest('.product-details');
    this.dialog = this.querySelector('dialog');
    this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:this.dataset.currency}).format(cents / 100);
    this.addEventListener('click', this.onClick, {signal:this.abort.signal});
    this.addEventListener('change', this.onChange, {signal:this.abort.signal});
    this.section?.addEventListener(StandardEvents.productSelect, this.onVariant, {signal:this.abort.signal});
    this.details?.addEventListener('change', event => {
      if (event.target.matches('input[name="quantity"]') && this.count === 1) this.render();
    }, {signal:this.abort.signal});
    this.details?.addEventListener('quantity-selector:update', () => {
      if (this.count === 1) this.render();
    }, {signal:this.abort.signal});
    this.dialog.addEventListener('close', () => this.lastFocused?.isConnected && this.lastFocused.focus({preventScroll:true}), {signal:this.abort.signal});
    this.render();
    this.hidden = false;
  }
  disconnectedCallback() {
    this.abort?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.details?.classList.remove('btk-mm-building');
    this.initialized = false;
  }
  text(key, value) { return String(this.t[key] || key).replace('{n}', String(value ?? '')); }
  get base() { return this.product.variants.find(v => Number(v.id) === this.baseId); }
  get missing() { for (let i=0; i<this.count-1; i++) if (!this.extras[i]) return i; return -1; }
  get items() { return [{product:this.product, variant:this.base}, ...this.extras.slice(0,this.count-1).filter(Boolean)]; }
  get usedProductIds() { return new Set([String(this.product.id), ...this.extras.filter((item,index)=>item && index!==this.slot).map(item=>String(item.product.id))]); }
  onVariant = async event => {
    if (event.target?.closest('product-card')) return;
    this.variantPending = true;
    this.render();
    const generation = this.generation = (this.generation || 0) + 1;
    try {
      const {detail} = await event.promise;
      if (generation !== this.generation) return;
      if (detail?.newProduct || String(detail?.productId || this.product.id) !== String(this.product.id)) {
        this.count = 1; this.hidden = true; this.details.classList.remove('btk-mm-building'); return;
      }
      this.baseId = Number(detail?.resource?.id || 0);
    } catch { this.baseId = 0; }
    finally { if (generation === this.generation) { this.variantPending = false; this.render(); } }
  };
  render() {
    this.querySelectorAll('button,input,select').forEach(control=>{control.disabled=false;});
    const mixed = this.count > 1;
    this.details?.classList.toggle('btk-mm-building', mixed);
    this.querySelector('[data-mm-tiers]').innerHTML = [1,2,3].map(n => `<label class="btk-mm-tier"><input type="radio" name="mix-${this.dataset.sectionId}" value="${n}" ${Math.min(3,this.count)===n?'checked':''}><span><strong>${escape(this.text(`tier_${n}`))}</strong><small>${escape(this.text(`tier_note_${n}`))}</small></span><span class="btk-mm-percent">${rate(n)}% ${escape(this.text('off'))}</span></label>`).join('');
    const rows = [];
    if (mixed) {
      if (this.base) rows.push(this.itemRow({product:this.product,variant:this.base}, -1));
      for (let i=0; i<this.count-1; i++) rows.push(this.extras[i] ? this.itemRow(this.extras[i],i) : `<button type="button" class="btk-mm-empty" data-mm-choose="${i}" aria-haspopup="dialog"><span aria-hidden="true">+</span><span>${escape(this.text('choose_item',i+2))}</span></button>`);
    }
    this.querySelector('[data-mm-items]').innerHTML = rows.join('');
    const ready = this.missing === -1 && this.base?.available && this.items.every(i=>i.variant?.available);
    const quantity = mixed ? 1 : Math.max(1,Number(this.details?.querySelector('input[name="quantity"]')?.value || 1));
    const percent = rate(mixed ? this.count : quantity);
    const sum = this.items.reduce((s,i)=>s+(i.variant?.price || 0)*quantity,0);
    const total = this.items.reduce((s,i)=>s+Math.round((i.variant?.price || 0)*quantity*(100-percent)/100),0);
    this.querySelector('[data-mm-total]').innerHTML = ready ? `<span>${escape(this.text('estimated_total'))}</span><span><del>${this.money(sum)}</del><strong>${this.money(total)}</strong></span>` : `<span>${escape(this.text('complete_selection'))}</span>`;
    const add = this.querySelector('[data-mm-add]');
    add.hidden = !mixed;
    add.disabled = this.busy || this.variantPending || !this.base?.available || !!this.uncertain;
    add.textContent = this.busy ? this.text('adding') : this.missing !== -1 ? this.text('choose_item',this.missing+2) : this.text('add_all',this.count);
    this.querySelector('[data-mm-more]').hidden = this.count < 3 || this.count >= MAX_ITEMS || this.missing !== -1;
    if (this.busy) this.querySelectorAll('button,input,select').forEach(control=>{control.disabled=true;});
  }
  itemRow(item,index) {
    const {product,variant} = item;
    const label = product.label || product.title;
    const available = product.allowedVariants || product.variants;
    const options = index < 0 ? `<p>${escape(variant.title)}</p>` : `<div class="btk-mm-options">${product.options.map((option,i) => {
      const values = [...new Set(available.filter(v=>v.available).map(v=>v.options[i]))];
      return `<label>${escape(option.name || option)}<select data-mm-option="${i}" data-mm-index="${index}" aria-label="${escape(this.text('item',index+2)+' '+(option.name || option))}">${values.map(value=>`<option value="${escape(value)}" ${variant.options[i]===value?'selected':''}>${escape(value)}</option>`).join('')}</select></label>`;
    }).join('')}</div>`;
    return `<div class="btk-mm-row"><img src="${escape(imageUrl(product,variant))}" alt="${escape(label)}" width="54" height="72" loading="lazy"><div><h3><a href="${root()}products/${escape(product.handle)}?variant=${variant.id}" target="_blank" rel="noopener">${escape(label)}</a></h3><strong>${this.money(variant.price)}</strong>${options}</div><div class="btk-mm-actions">${index<0 ? `<button type="button" data-mm-edit>${escape(this.text('edit'))}</button>` : `<button type="button" data-mm-choose="${index}">${escape(this.text('change'))}</button><button type="button" data-mm-remove="${index}" aria-label="${escape(this.text('remove_item',index+2))}" title="${escape(this.text('remove_item',index+2))}">&#215;</button>`}</div></div>`;
  }
  onChange = event => {
    if (event.target.matches('[data-mm-tiers] input')) {
      const n = Number(event.target.value);
      this.count = n === 3 ? Math.max(3,this.count) : n;
      this.extras.length = this.count - 1;
      this.render();
      this.querySelector(`[data-mm-tiers] input[value="${n}"]`).focus({preventScroll:true});
    }
    if (event.target.matches('[data-mm-option]')) {
      const index = Number(event.target.dataset.mmIndex), optionIndex = Number(event.target.dataset.mmOption);
      const item = this.extras[index], desired = [...item.variant.options];
      desired[optionIndex] = event.target.value;
      const match = item.product.allowedVariants.find(v=>v.available && v.options.every((value,i)=>value===desired[i]));
      if (!match) { this.error(this.text('combination_unavailable')); this.render(); return; }
      item.variant = match;
      this.error(''); this.render();
      this.querySelector(`[data-mm-index="${index}"][data-mm-option="${optionIndex}"]`)?.focus({preventScroll:true});
    }
  };
  onClick = event => {
    const button = event.target.closest('button');
    if (event.target === this.dialog) {
      const r = this.dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right) this.dialog.close();
    }
    if (!button || this.busy) return;
    if (button.hasAttribute('data-mm-close')) this.dialog.close();
    if (button.hasAttribute('data-mm-category')) this.loadPickerCategory(button.dataset.mmCategory);
    if (button.hasAttribute('data-mm-load-more')) this.loadPickerCategory(this.activeCategory, true);
    if (button.hasAttribute('data-mm-choose')) this.openPicker(Number(button.dataset.mmChoose));
    if (button.hasAttribute('data-mm-more') && this.count < MAX_ITEMS) { this.count++; this.render(); this.openPicker(this.count-2); }
    if (button.hasAttribute('data-mm-remove')) { this.extras.splice(Number(button.dataset.mmRemove),1); this.count--; this.render(); }
    if (button.hasAttribute('data-mm-edit')) this.details.querySelector('variant-picker')?.scrollIntoView({behavior:'smooth',block:'center'});
    if (button.hasAttribute('data-mm-pick')) {
      const choice = this.choices.find(p=>String(p.product.id)===button.dataset.mmPick);
      if (!choice) return;
      this.extras[this.slot] = {product:choice.product,variant:choice.variant};
      this.dialog.close(); this.error(''); this.render();
      this.querySelector(`[data-mm-choose="${this.slot}"]`)?.focus({preventScroll:true});
    }
    if (button.hasAttribute('data-mm-add')) this.missing !== -1 ? this.openPicker(this.missing) : this.addAll();
  };
  async loadCatalog() {
    if (this.catalog) return this.catalog;
    if (this.catalogPromise) return this.catalogPromise;
    this.catalogPromise = (async () => {
      const response = await fetch(this.dataset.catalogUrl);
      if (!response.ok) throw new Error('Catalog unavailable');
      const {products} = await response.json();
      const settled = await Promise.allSettled(products.map(async meta => {
        const response = await fetch(`${root()}products/${encodeURIComponent(meta.handle)}.js`);
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        const allowedVariants = product.variants.filter(v=>v.available && !v.requires_selling_plan && (v.quantity_rule?.min || 1)===1 && (!meta.variantPattern || new RegExp(meta.variantPattern,'i').test(v.option1)));
        if (!allowedVariants.length) throw new Error('Sold out');
        return {...product,...meta,allowedVariants};
      }));
      this.catalog = settled.filter(r=>r.status==='fulfilled').map(r=>r.value);
      if (!this.catalog.length) { this.catalog=null; throw new Error('No products loaded'); }
      return this.catalog;
    })().finally(()=>{this.catalogPromise=null;});
    return this.catalogPromise;
  }
  preferredVariant(product) {
    const size = sizeKey(variantSize(this.product,this.base));
    const color = String(this.base?.option1 || '').toLowerCase();
    const neutral = /black|white|grey|gray|gery|khaki/;
    const variants = product.allowedVariants.filter(variant=>!size || sizeKey(variantSize(product,variant))===size);
    variants.sort((a,b)=>Number(color.includes(String(b.option1 || '').toLowerCase()))-Number(color.includes(String(a.option1 || '').toLowerCase())) || Number(neutral.test(String(b.option1 || '').toLowerCase()))-Number(neutral.test(String(a.option1 || '').toLowerCase())) || a.price-b.price);
    return variants[0];
  }
  normalizeCollectionProduct(raw, category) {
    if (!raw || /\b(women|women's|womens|female|ladies)\b/i.test(raw.title || '')) return null;
    const variants = (raw.variants || []).map(variant=>({
      ...variant,
      options: Array.isArray(variant.options) ? variant.options : [variant.option1,variant.option2,variant.option3].filter(value=>value != null),
      price: typeof variant.price === 'string' ? Math.round(Number(variant.price) * 100) : Math.round(Number(variant.price || 0)),
      quantity_rule: variant.quantity_rule || {min:1},
    }));
    const product = {
      ...raw,
      variants,
      featured_image: raw.featured_image?.src || raw.featured_image || raw.images?.[0]?.src || raw.images?.[0] || '',
      images: (raw.images || []).map(image=>image?.src || image),
    };
    const allowedVariants = variants.filter(variant=>variant.available && !variant.requires_selling_plan && (variant.quantity_rule?.min || 1)===1 && selectedCategory(product,variant)===category);
    if (!allowedVariants.length) return null;
    const title = String(raw.title || '').toLowerCase();
    return {
      ...product,
      label: raw.title,
      category,
      style: /business|lapel|button|jacquard|polo/.test(title) ? 'smart' : 'casual',
      season: /hoodie|sweatshirt|jacket|coat|long.sleeve|winter|sweat/.test(title) ? 'cool' : 'warm',
      netUnits: 0,
      allowedVariants,
    };
  }
  async loadCollectionPage(handle,page) {
    const cacheKey = `${handle}:${page}`;
    if (this.collectionCache.has(cacheKey)) return this.collectionCache.get(cacheKey);
    const request = async attempt => {
      const url = `${root()}collections/${encodeURIComponent(handle)}/products.json?limit=${COLLECTION_PAGE_SIZE}&page=${page}`;
      const response = await fetch(url,{headers:{Accept:'application/json'},credentials:'same-origin',signal:this.abort.signal});
      if (response.status===429 && attempt<2) {
        await new Promise(resolve=>setTimeout(resolve,500*(attempt+1)));
        return request(attempt+1);
      }
      if (!response.ok) throw new Error(`Collection unavailable: ${handle}`);
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    };
    const products = await request(0);
    this.collectionCache.set(cacheKey,products);
    return products;
  }
  categoryConfig(key) {
    const button = [...this.querySelectorAll('[data-mm-category]')].find(item=>item.dataset.mmCategory===key);
    if (!button) return null;
    return {
      key,
      category: button.dataset.mmProductCategory || '',
      handles: String(button.dataset.mmCollections || '').split(',').filter(Boolean),
    };
  }
  updateCategoryButtons(key) {
    this.querySelectorAll('[data-mm-category]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.mmCategory===key)));
  }
  renderPickerChoices(emptyKey='none_in_category') {
    const target = this.querySelector('[data-mm-products]');
    target.innerHTML = this.choices.length ? this.choices.map(({product,variant})=>`<button type="button" class="btk-mm-product" data-mm-pick="${product.id}"><img src="${escape(imageUrl(product,variant))}" alt="${escape(product.label || product.title)}" width="68" height="86" loading="lazy"><span><strong>${escape(product.label || product.title)}</strong><small>${escape(variant.title)}</small><span>${this.money(variant.price)}</span></span><span aria-hidden="true">+</span></button>`).join('') : `<p class="btk-mm-picker-empty">${escape(this.text(emptyKey))}</p><a href="${root()}collections/mens-clothing">${escape(this.text('browse'))}</a>`;
  }
  async loadPickerCategory(key,append=false) {
    const config = this.categoryConfig(key);
    if (!config) return;
    const generation = ++this.pickerGeneration;
    const target = this.querySelector('[data-mm-products]');
    const loadMore = this.querySelector('[data-mm-load-more]');
    const page = append ? this.categoryPage + 1 : 1;
    this.activeCategory = key;
    this.updateCategoryButtons(key);
    loadMore.hidden = true;
    loadMore.disabled = true;
    loadMore.textContent = this.text(append ? 'loading_more' : 'load_more');
    if (!append) {
      this.choices = [];
      target.textContent = this.text('loading');
    }
    try {
      let hasMore = false;
      if (key==='recommended') {
        this.choices = this.dataset.womenswear === 'true' ? [] : this.rank(await this.loadCatalog());
      } else {
        const used = this.usedProductIds;
        const seen = new Set(append ? this.choices.map(choice=>String(choice.product.id)) : []);
        let nextPage = page;
        const requestLimit = append ? 1 : INITIAL_CATEGORY_PAGES;
        for (let request=0; request<requestLimit; request++,nextPage++) {
          const pages = await Promise.all(config.handles.map(handle=>this.loadCollectionPage(handle,nextPage)));
          if (generation!==this.pickerGeneration) return;
          const additions = [];
          for (const raw of pages.flat()) {
            if (used.has(String(raw.id)) || seen.has(String(raw.id))) continue;
            const product = this.normalizeCollectionProduct(raw,config.category);
            if (!product) continue;
            const variant = this.preferredVariant(product);
            if (!variant) continue;
            additions.push({product,variant});
            seen.add(String(product.id));
          }
          this.choices.push(...additions);
          this.categoryPage = nextPage;
          hasMore = pages.some(products=>products.length===COLLECTION_PAGE_SIZE);
          if (!hasMore || append || this.choices.length>=MIN_CATEGORY_CHOICES) break;
        }
      }
      if (generation!==this.pickerGeneration) return;
      this.renderPickerChoices(key==='recommended' ? 'none_available' : 'none_in_category');
      loadMore.hidden = !hasMore;
    } catch {
      if (generation!==this.pickerGeneration) return;
      if (!append) target.textContent = this.text('load_failed');
      loadMore.hidden = true;
    } finally {
      if (generation===this.pickerGeneration) {
        loadMore.disabled = false;
        loadMore.textContent = this.text('load_more');
      }
    }
  }
  rank(catalog) {
    const used = this.usedProductIds;
    const category = selectedCategory(this.product,this.base);
    const priorities = {hoodie:['pants','tee','shorts','shirt','hoodie'],tee:['pants','shorts','hoodie','shirt','tee'],shirt:['pants','shorts','tee','hoodie','shirt'],pants:['shirt','tee','hoodie','shorts','pants'],shorts:['tee','shirt','hoodie','pants','shorts'],set:['tee','shirt','hoodie','pants','shorts']}[category];
    const season = /hoodie|long.sleeve|winter|sweat/i.test(this.product.title) ? 'cool' : 'warm';
    const style = /business|lapel|button|jacquard/.test(this.product.title.toLowerCase()) ? 'smart' : 'casual';
    return catalog.filter(product=>!used.has(String(product.id))).map(product=>{
      const variant = this.preferredVariant(product);
      const option = String(variant?.option1 || '').toLowerCase();
      const compatibility = Number(product.style===style) + Number(product.season===season || product.season==='all') + Number(variant && /black|white|grey|gray|gery|khaki/.test(option));
      const categoryIndex = priorities.indexOf(product.category);
      return {product,variant,categoryRank:categoryIndex<0 ? priorities.length : categoryIndex,compatibility};
    }).filter(item=>item.variant).sort((a,b)=>a.categoryRank-b.categoryRank || b.compatibility-a.compatibility || b.product.netUnits-a.product.netUnits || a.variant.price-b.variant.price);
  }
  async openPicker(slot) {
    this.slot = slot;
    this.lastFocused = document.activeElement;
    this.querySelector('[data-mm-title]').textContent = this.text('choose_item',slot+2);
    const target = this.querySelector('[data-mm-products]');
    target.textContent = this.text('loading');
    if (!this.dialog.open) this.dialog.showModal();
    await this.loadPickerCategory(this.activeCategory || 'recommended');
  }
  error(message) { const node=this.querySelector('[data-mm-error]'); node.textContent=message; node.hidden=!message; }
  async addAll() {
    if (this.busy || this.variantPending || !this.base?.available || this.missing !== -1 || this.uncertain) return;
    this.busy = true; this.error(''); this.render();
    const items = this.items.map(item=>({id:Number(item.variant.id),quantity:1}));
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(()=>{});
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(item=>({merchandiseId:String(item.id),quantity:1})),promise:deferred.promise}));
    let addSucceeded = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el=>el.dataset.sectionId))].slice(0,5);
      const response = await fetch(`${root()}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items,sections,sections_url:location.pathname})});
      const result = await response.json();
      addSucceeded = response.ok;
      const cartResponse = await fetch(`${root()}cart.js`,{cache:'no-store'});
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'product-form-component',sourceId:`btk-mix-${this.product.id}`,itemCount:items.length,productId:String(this.product.id),sections:result.sections,didError:!response.ok}});
      if (!response.ok) { this.uncertain = true; throw new Error(result.description || result.message || this.text('add_failed')); }
    } catch (error) {
      deferred.reject(error);
      this.uncertain = true;
      this.error(`${error.message}. ${addSucceeded ? this.text('added_check_cart') : this.text('check_cart')}`);
      this.dispatchEvent(new CartErrorEvent({error:error.message,code:'SERVICE_UNAVAILABLE'}));
    } finally { this.busy=false; this.render(); }
  }
}
if (!customElements.get('btk-mix-match')) customElements.define('btk-mix-match', BtkMixMatch);
